import {
  AudioModule,
  getRecordingPermissionsAsync,
  requestNotificationPermissionsAsync,
  requestRecordingPermissionsAsync,
  setAudioModeAsync,
} from 'expo-audio';
import type { AudioRecorder as AudioRecorderType, RecordingOptions } from 'expo-audio';
import { Platform } from 'react-native';

import { sentryLogger } from '@/adapters/logger/sentryLogger';

import { PLAYBACK_MODE, RECORDING_MODE, VOICE_RECORDING_PRESET } from './config';
import {
  createNotificationPermissionDeniedError,
  createStartCancelledError,
  isStartCancelledError,
} from './errors';
import { type AudioRecorderAdapter, type RecorderState, type StartRecordingOptions } from './types';

const flattenRecordingOptions = (options: RecordingOptions) => {
  const commonOptions = {
    extension: options.extension,
    sampleRate: options.sampleRate,
    numberOfChannels: options.numberOfChannels,
    bitRate: options.bitRate,
    isMeteringEnabled: options.isMeteringEnabled ?? false,
  };

  if (Platform.OS === 'ios') {
    return { ...commonOptions, ...options.ios };
  }
  if (Platform.OS === 'android') {
    return { ...commonOptions, ...options.android };
  }
  return { ...commonOptions, ...options.web };
};

type AudioRecorderConstructorType = new (options: unknown) => AudioRecorderType;

const resolveAudioRecorderConstructor = (): AudioRecorderConstructorType => {
  // SAFETY: expo-audio's native modules expose the recorder class as
  // AudioModule.AudioRecorder, while the web module exports it as
  // AudioRecorderWeb (see build/AudioModule.web.js). We must branch on the
  // platform-specific export name, but the union is not reflected in the
  // package types, so we cast through unknown.
  const audioModule = AudioModule as unknown as {
    AudioRecorder?: AudioRecorderConstructorType;
    AudioRecorderWeb?: AudioRecorderConstructorType;
  };

  const constructor =
    Platform.OS === 'web' ? audioModule.AudioRecorderWeb : audioModule.AudioRecorder;

  if (!constructor) {
    throw new Error('AudioRecorder constructor is not available on this platform');
  }

  return constructor;
};

const METERING_INTERVAL_MS = 150;
const METERING_MIN_DB = -60;
const START_RECORDING_DEFAULT_TIMEOUT_MS = 15_000;
const ORPHAN_TEARDOWN_DEFAULT_TIMEOUT_MS = 2_000;
const RETRY_TIMEOUT_MULTIPLIER = 2;
const PLATFORM_RECORDING_OPTIONS = flattenRecordingOptions(VOICE_RECORDING_PRESET);

const noop = (): void => {};

type PrepareStage = 'setAudioMode' | 'createRecorder' | 'prepareToRecordAsync';

const PREPARE_STAGE_BREADCRUMBS: Record<PrepareStage, string> = {
  setAudioMode: 'Recording: setting audio mode',
  createRecorder: 'Recording: creating recorder',
  prepareToRecordAsync: 'Recording: preparing recorder',
};

const normalizeMetering = (db: number | undefined): number => {
  if (db == null) return 0;

  const clamped = Math.max(METERING_MIN_DB, Math.min(0, db));

  return (clamped - METERING_MIN_DB) / -METERING_MIN_DB;
};

interface AudioRecorderState {
  recorder: AudioRecorderType | null;
  isProcessing: boolean;
  cancelStart: (() => void) | null;
  onRecordingError: ((error: string) => void) | null;
  onMeteringUpdate: ((level: number) => void) | null;
  onStateUpdate: ((state: RecorderState) => void) | null;
  meteringInterval: ReturnType<typeof setInterval> | null;
  statusListener: { remove: () => void } | null;
}

const createAudioRecorderAdapter = (): AudioRecorderAdapter => {
  const state: AudioRecorderState = {
    recorder: null,
    isProcessing: false,
    cancelStart: null,
    onRecordingError: null,
    onMeteringUpdate: null,
    onStateUpdate: null,
    meteringInterval: null,
    statusListener: null,
  };

  const startMeteringPolling = (): void => {
    stopMeteringPolling();

    if (!state.onMeteringUpdate) return;

    state.meteringInterval = setInterval(() => {
      if (!state.recorder || !state.onMeteringUpdate) return;

      const status = state.recorder.getStatus();

      if (!status.isRecording) return;

      state.onMeteringUpdate(normalizeMetering(status.metering));
    }, METERING_INTERVAL_MS);
  };

  const stopMeteringPolling = (): void => {
    if (!state.meteringInterval) return;

    clearInterval(state.meteringInterval);
    state.meteringInterval = null;
  };

  const restorePlaybackMode = async (): Promise<void> => {
    try {
      await setAudioModeAsync(PLAYBACK_MODE);
    } catch (error) {
      // Setting the audio mode is expected to fail while the app is backgrounded,
      // and the branch is non-fatal, so the exception alone carries the detail.
      sentryLogger.addBreadcrumb('Failed to restore playback mode');
      sentryLogger.captureException(error);
    }
  };

  const tearDownRecorder = async (): Promise<void> => {
    stopMeteringPolling();
    state.statusListener?.remove();
    state.statusListener = null;
    if (!state.recorder) return;
    try {
      await state.recorder.stop();
    } catch (stopError) {
      sentryLogger.captureException(stopError);
    }
    state.recorder = null;
    await restorePlaybackMode();
  };

  return {
    requestPermission: async (): Promise<boolean> => {
      const { status: existingStatus } = await getRecordingPermissionsAsync();
      if (existingStatus === 'granted') {
        return true;
      }
      const { status } = await requestRecordingPermissionsAsync();
      return status === 'granted';
    },

    setOnRecordingError: (callback: (error: string) => void): void => {
      state.onRecordingError = callback;
    },

    setOnStateUpdate: (callback: ((state: RecorderState) => void) | null): void => {
      state.onStateUpdate = callback;
    },

    startRecording: async ({
      timeoutMs = START_RECORDING_DEFAULT_TIMEOUT_MS,
      orphanTeardownMs = Math.min(ORPHAN_TEARDOWN_DEFAULT_TIMEOUT_MS, timeoutMs),
    }: StartRecordingOptions = {}): Promise<void> => {
      if (state.isProcessing) {
        throw new Error('Recording operation in progress');
      }
      state.isProcessing = true;

      // Preparation can take tens of seconds on slow devices, and the processing
      // lock blocks every other operation while it runs. Without this the user
      // has no way to back out of a start that is going nowhere.
      const abortRef = {} as { reject: ((error: unknown) => void) | null };
      const abortPromise = new Promise<never>((_, reject) => {
        abortRef.reject = reject;
      });
      abortPromise.catch(noop);
      state.cancelStart = () => abortRef.reject?.(createStartCancelledError());

      const isTimeoutError = (error: unknown): boolean =>
        error instanceof Error && /timed out/.test(error.message);

      const startRecording = async (isRetry: boolean): Promise<AudioRecorderType> => {
        const attempt = isRetry ? 2 : 1;
        const attemptTimeoutMs = isRetry ? timeoutMs * RETRY_TIMEOUT_MULTIPLIER : timeoutMs;
        const progress = { stage: 'setAudioMode' as PrepareStage, startedAt: Date.now() };

        const enterStage = (stage: PrepareStage): void => {
          progress.stage = stage;
          progress.startedAt = Date.now();
          sentryLogger.addBreadcrumb(PREPARE_STAGE_BREADCRUMBS[stage], { attempt });
        };

        if (state.recorder) {
          try {
            await state.recorder.stop();
          } catch (error) {
            sentryLogger.captureMessage(
              'Failed to stop previous recorder before starting new recording',
              'warning',
            );
            sentryLogger.captureException(error);
          }
          state.recorder = null;
        }

        await restorePlaybackMode();

        if (Platform.OS === 'android') {
          sentryLogger.addBreadcrumb('Recording: requesting notification permission');
          const { status } = await requestNotificationPermissionsAsync();
          if (status !== 'granted') {
            throw createNotificationPermissionDeniedError();
          }
        }

        const prepareChain = (async (): Promise<AudioRecorderType> => {
          enterStage('setAudioMode');
          await setAudioModeAsync(RECORDING_MODE);
          enterStage('createRecorder');
          const AudioRecorderConstructor = resolveAudioRecorderConstructor();
          const newRecorder = new AudioRecorderConstructor(PLATFORM_RECORDING_OPTIONS);
          enterStage('prepareToRecordAsync');
          await newRecorder.prepareToRecordAsync();
          return newRecorder;
        })();

        const timeoutRef = {} as { handle: ReturnType<typeof setTimeout> };
        const timeoutPromise = new Promise<never>((_, reject) => {
          timeoutRef.handle = setTimeout(
            () => reject(new Error(`startRecording timed out after ${attemptTimeoutMs}ms`)),
            attemptTimeoutMs,
          );
        });

        // Only called once the attempt is abandoned. The stop still runs to
        // completion even if the bounded wait below gives up on it first.
        const tearDownOrphan = async (): Promise<void> => {
          const stopped = prepareChain.then((orphan) => orphan.stop()).catch(noop);

          await Promise.race([
            stopped,
            new Promise<void>((resolve) => setTimeout(resolve, orphanTeardownMs)),
          ]);
        };

        try {
          return await Promise.race([prepareChain, timeoutPromise, abortPromise]).finally(() =>
            clearTimeout(timeoutRef.handle),
          );
        } catch (error) {
          if (isTimeoutError(error)) {
            sentryLogger.addBreadcrumb('Recording: start timed out', {
              stage: progress.stage,
              stageElapsedMs: Date.now() - progress.startedAt,
              attempt,
              timeoutMs: attemptTimeoutMs,
            });
          }

          if (isStartCancelledError(error)) {
            // The user is waiting on this; release the recorder in the background
            // rather than making them sit through the teardown bound.
            void prepareChain.then((orphan) => orphan.stop()).catch(noop);

            throw error;
          }

          // The previous native recorder still holds the microphone; releasing it
          // before retrying stops the second attempt contending with the first.
          await tearDownOrphan();

          if (isRetry || !isTimeoutError(error)) {
            throw error;
          }

          sentryLogger.captureMessage('Retrying startRecording after timeout', 'warning');
          sentryLogger.addBreadcrumb('Recording: retrying after timeout', { attempt });

          return startRecording(true);
        }
      };

      try {
        const recorder = await startRecording(false);

        state.statusListener = recorder.addListener('recordingStatusUpdate', (status) => {
          if (status.mediaServicesDidReset) {
            const message = 'Recording interrupted by media services reset';
            sentryLogger.addBreadcrumb('Recording: media services reset');
            sentryLogger.captureMessage(message, 'warning');
            state.onRecordingError?.(message);
            void tearDownRecorder();
            return;
          }

          if (status.hasError) {
            const message = status.error ?? 'Unknown recording error';
            sentryLogger.captureMessage(`Recording failed mid-session: ${message}`, 'error');
            state.onRecordingError?.(message);
            void tearDownRecorder();
            return;
          }

          if (status.isFinished) {
            const message = 'Recording stopped unexpectedly';
            sentryLogger.captureMessage(`Recording failed mid-session: ${message}`, 'error');
            state.onRecordingError?.(message);
            void tearDownRecorder();
          }
        });

        recorder.record();
        state.recorder = recorder;
        startMeteringPolling();
      } catch (error) {
        await restorePlaybackMode();
        throw error;
      } finally {
        state.cancelStart = null;
        state.isProcessing = false;
      }
    },

    setOnMeteringUpdate: (callback: ((level: number) => void) | null): void => {
      state.onMeteringUpdate = callback;
    },

    stopRecording: async (): Promise<{
      uri: string;
      durationMs: number | null;
    }> => {
      if (state.isProcessing) {
        throw new Error('Recording operation in progress');
      }
      if (!state.recorder) {
        throw new Error('No recording in progress');
      }
      state.isProcessing = true;
      state.onRecordingError = null;
      stopMeteringPolling();
      state.statusListener?.remove();
      state.statusListener = null;

      try {
        const status = state.recorder.getStatus();
        await state.recorder.stop();
        const uri = state.recorder.uri;

        if (!uri) {
          throw new Error('Recording failed: no URI returned');
        }

        const durationMs = status.durationMillis;

        return { uri, durationMs };
      } finally {
        state.recorder = null;
        state.isProcessing = false;
        await restorePlaybackMode();
      }
    },

    cancelRecording: async (): Promise<void> => {
      if (state.isProcessing) {
        // A start in flight owns the lock; abort it instead of silently no-oping.
        state.cancelStart?.();

        return;
      }
      state.isProcessing = true;
      state.onRecordingError = null;
      stopMeteringPolling();
      state.statusListener?.remove();
      state.statusListener = null;

      if (state.recorder) {
        try {
          await state.recorder.stop();
        } catch (error) {
          sentryLogger.captureMessage('Failed to stop recorder during cancel', 'warning');
          sentryLogger.captureException(error);
        }
        state.recorder = null;
      }

      try {
        await restorePlaybackMode();
      } finally {
        state.isProcessing = false;
      }
    },

    pauseRecording: async (): Promise<void> => {
      if (state.isProcessing) return;
      if (!state.recorder) {
        throw new Error('No recording in progress');
      }
      stopMeteringPolling();
      state.recorder.pause();
    },

    resumeRecording: async (): Promise<void> => {
      if (state.isProcessing) return;
      if (!state.recorder) {
        throw new Error('No recording in progress');
      }
      state.recorder.record();
      startMeteringPolling();
    },
  };
};

const audioRecorder = createAudioRecorderAdapter();

export { createAudioRecorderAdapter, audioRecorder };
